import { isWindowGated, type Channel } from "./types";
import { HOURS } from "./window";

/**
 * The Calls agent's decision function (Agent Brains section 5.10).
 *
 * Section 5.10's charter, verbatim (apps/web/src/lib/agents.ts): mission
 * "Missed-call text-back first, spoken conversation later."; never "Never
 * claims to be a person. Same gates as text, on a stricter latency budget.";
 * handoff "Shares Conversations' full stack."; produces "Answered calls and
 * recovered missed ones."; blockedBy "Premium, and last by design. Everything
 * it does is the text stack plus speech, on a stricter latency budget."
 *
 * THE CHARTER SEQUENCES ITSELF. "Missed-call text-back first, spoken
 * conversation later" names two halves and an order. Spoken conversation
 * needs real-time speech infrastructure (STT/TTS, a telephony provider that
 * can bridge audio) that a repo-wide grep confirms does not exist anywhere in
 * this codebase (no `twilio`, no `stt`/`tts`, no telephony dependency of any
 * kind in any `package.json`): that half stays exactly as blocked as the
 * registry says. Missed-call text-back is the other half, and it is a text
 * message on a channel that already exists in this schema (`"sms"`,
 * `channelEnum`, `packages/db/src/schema.ts`), not a speech feature. The
 * charter's own handoff line, "Shares Conversations' full stack," is the
 * instruction to reuse the existing window/arbiter/gate-chain machinery
 * rather than invent a second send path for it. See FINDINGS.md TA-188 for
 * the full account of what a grep for a telephony vendor did and did not
 * find.
 *
 * WHAT THIS FILE DOES NOT DO. There is no telephony webhook anywhere in this
 * codebase, and building one would mean inventing a vendor payload shape this
 * task was explicitly told not to invent (FINDINGS.md TA-188). So
 * `MissedCallEvent` below is a plain internal type, not a Twilio/vendor
 * shape: whichever provider is eventually chosen (a chassis-fork-time
 * decision, the same deferral F-23 already makes for framework choices)
 * adapts its own webhook payload into this type before anything here ever
 * sees it, the same role `IngestEvent` (`@ackra/pipeline`) already plays for
 * Meta webhooks in `ingestEvents`.
 *
 * This file is the decision half only, the identical split
 * `decideCampaignWave` (campaign.ts) and `decideAppointmentReminder`
 * (appointmentReminder.ts) already establish: given one missed-call event and
 * what is known about the caller, decide whether texting them back right now
 * is safe. It never composes anything, never queries a database, never sends
 * anything. The machinery that resolves a caller's phone number to a contact,
 * finds or creates their sms thread, drives an eligible text-back through the
 * real send path, and raises a loud alert when an automatic text-back is not
 * possible sits in `packages/agents/src/missedCallTextBack.ts`.
 *
 * "ACTIVE CONVERSATION", THE SAME DEFINITION AGAIN. Section 7 says a
 * Shepherd nudge, a Campaign wave, a live Reply thread and a review request
 * all compete for the same customer through the one arbiter, and this
 * charter's own mission line ("text-back first") only makes sense as a
 * fallback for a caller nobody is already talking to. Rather than inventing a
 * third definition of "still live" next to `followUp.ts`'s and
 * `campaign.ts`'s, this file reuses the same primitives against the caller's
 * OWN sms thread: frozen, a pending human draft, or the customer being owed a
 * reply (tie goes to the customer). Scoped to the sms thread alone, not every
 * channel the contact might be talking to us on: `decideCampaignWave` makes
 * the identical scoping choice for its own target channel, and checking every
 * other channel here would need cross-channel data no other agent in this
 * codebase plumbs either. Logged as a shared, known limitation in FINDINGS.md
 * TA-188, not a gap specific to this file.
 */

export type MissedCallTextBackAction =
  | "eligible"
  | "excluded_no_contact_match"
  | "excluded_suppressed"
  | "excluded_active_conversation"
  | "excluded_no_sms_consent"
  | "excluded_recently_texted_back";

export interface MissedCallTextBackDecision {
  readonly action: MissedCallTextBackAction;
  /** One line a human or a log line can read without more context. */
  readonly reason: string;
}

/**
 * A missed-call event, as a plain internal type. No vendor shape.
 *
 * `channel` is the channel the text-back would go out on, not the (nonexistent,
 * unmodeled) channel the call itself arrived on: this schema's `Channel` enum
 * has no telephony member, because nothing in this codebase can receive or
 * place a call. In practice this is always `"sms"` today, but it is a real
 * field rather than a hardcoded literal so that a caller (a future webhook
 * adapter) is the one who says which text channel to answer on, not this
 * file assuming it.
 */
export interface MissedCallEvent {
  readonly channel: Channel;
  readonly occurredAt: Date;
  /**
   * The calling telephony vendor's own unique id for this call (a Twilio
   * CallSid or equivalent). Required, not because a vendor is integrated
   * (none is), but because every real telephony API assigns one, and the
   * machinery that eventually adapts a real webhook into this type needs a
   * stable value to key idempotency on, the same role
   * `IngestEvent.platformMessageId` plays for a Meta message.
   */
  readonly providerCallId: string;
  /**
   * The caller's number, in whatever form the (nonexistent) telephony vendor
   * hands it over. This is the one fact the whole feature turns on: it is
   * what the machinery in `packages/agents/src/missedCallTextBack.ts` matches
   * against `contacts.phone` to find `hasContactMatch`. Kept as a raw string
   * rather than normalized here, the same reasoning `IngestEvent.senderId`
   * carries: normalization (E.164, stripped formatting) is the adapter's job,
   * not this plain internal type's.
   */
  readonly callerPhone: string;
}

export interface MissedCallCandidateInput {
  readonly channel: Channel;
  /**
   * The calling number matched an existing `contacts.phone` row. False means
   * an unrecognised caller: no contact, no consent, nothing on file to
   * text-back with. CLAUDE.md section 8 is explicit that SMS requires a
   * contact "captured AND consented", and an unmatched number has cleared
   * neither bar.
   */
  readonly hasContactMatch: boolean;
  /**
   * Express consent to be texted, captured at intake. NOT the same claim as
   * `CampaignAudienceCriteria`'s reuse of `threads.has_marketing_optin` for a
   * marketing broadcast to an existing list: this is `escalation.ts`'s
   * `hasSmsConsent` concept, reaching a caller who may never have texted this
   * shop before, exactly the `fallback_to_sms` situation. F-20 says SMS's own
   * consent regime is specced but not yet built, and no column recording
   * express SMS consent exists in this schema today (the identical gap
   * `escalationSweep.ts` names for its own `hasSmsConsent`, hardcoded false,
   * TA-180). This field stays real and tested here so the decision logic is
   * correct the day that column exists; see FINDINGS.md TA-188 for how the
   * machinery wires it today.
   */
  readonly hasSmsConsent: boolean;
  /** Global suppression list (section 7): bereavement, an opt-out, a
   * sensitive life change. Beats every priority, including a missed call. */
  readonly isSuppressed: boolean;
  /** An open row in `thread_freezes` on the caller's sms thread (the
   * injection freeze). T3, human-cleared only. */
  readonly isFrozen: boolean;
  /** A `pending_approval` draft sits on the caller's sms thread: a human is
   * already answering this contact right now. */
  readonly hasPendingApprovalDraft: boolean;
  /** On the caller's sms thread, if one exists. Null when this would be
   * their first-ever text contact with the shop. */
  readonly lastCustomerMessageAt: Date | null;
  readonly lastOutboundAt: Date | null;
  /** The last time THIS agent sent a missed-call text-back to this contact,
   * regardless of which call prompted it. Null if never. */
  readonly lastTextBackSentAt: Date | null;
  readonly now: Date;
}

function plan(action: MissedCallTextBackAction, reason: string): MissedCallTextBackDecision {
  return { action, reason };
}

/**
 * How long a text-back already sent suppresses another one, so three missed
 * calls in five minutes (a customer redialing, or leaving no voicemail and
 * trying again) get one text-back, not three. Section 5.10 names no such
 * number, so this is an implementer's placeholder in the same shape
 * `FOLLOWUP_SILENCE_MS` and `APPOINTMENT_REMINDER_DAY_BEFORE_MS` already
 * carry: an hour is long enough that a burst of redials in the same sitting
 * collapses to one message, short enough that a genuinely separate missed
 * call tomorrow is never mistaken for a duplicate.
 * AWAITING RATIFICATION (TA-188): unmeasured against real call patterns.
 */
export const MISSED_CALL_TEXT_BACK_COOLDOWN_MS = 1 * HOURS;

/**
 * Whether the caller's own sms thread is, right now, a conversation a
 * missed-call text-back must not interrupt. Three independent ways in, any
 * one sufficient, the same shape `decideCampaignWave`'s `isActiveConversation`
 * already establishes for its own target channel:
 *
 * 1. Frozen (a quarantined thread is treated as active, conservatively).
 * 2. A pending human draft (someone is already answering this contact).
 * 3. The customer spoke last, or spoke at all and never got a reply: we owe
 *    them, and a generic missed-call template is never the answer to that
 *    debt (tie goes to the customer).
 *
 * `isWindowGated` is checked too, for the same forward-compatible reason
 * `MissedCallEvent.channel` is a real field rather than a hardcoded literal:
 * `"sms"` is never window-gated today, so this branch never fires in
 * practice, but a text-back channel this file did not anticipate should not
 * silently skip the Meta-window half of "still live" the day one exists.
 */
function isActiveConversation(c: MissedCallCandidateInput): boolean {
  if (c.isFrozen) return true;
  if (c.hasPendingApprovalDraft) return true;

  if (isWindowGated(c.channel)) {
    // Deliberately inlined rather than importing `windowState`: pulling in
    // the full Meta window machinery for a branch that cannot fire against
    // any channel this file uses today would be dead code dressed as
    // coverage. The moment a window-gated text-back channel exists, this is
    // the line to replace with a real `windowState` call.
    if (c.lastCustomerMessageAt !== null) {
      const elapsed = c.now.getTime() - c.lastCustomerMessageAt.getTime();
      if (elapsed < 24 * HOURS) return true;
    }
  }

  if (c.lastCustomerMessageAt !== null) {
    if (c.lastOutboundAt === null) return true;
    if (c.lastCustomerMessageAt.getTime() >= c.lastOutboundAt.getTime()) return true;
  }

  return false;
}

/**
 * Section 0: precision beats recall. The only way out of this function with
 * `eligible` is clearing every check in order; any one of them being true is
 * enough to withhold. Order matters and is deliberate:
 *
 * 1. `excluded_no_contact_match`: a structural precondition, checked before
 *    any other fact, because none of the others can even be evaluated
 *    honestly about someone this system cannot identify.
 * 2. `excluded_suppressed`: section 7, "enforced globally... beats every
 *    priority."
 * 3. `excluded_active_conversation`: the mission's own sequencing, "text-back
 *    first" only makes sense as what happens when nobody is already
 *    attending to this caller.
 * 4. `excluded_no_sms_consent`: CLAUDE.md section 8's explicit rule, "SMS or
 *    email only if a contact was captured AND consented," checked
 *    unconditionally rather than deferred to the send path's own window
 *    gate, the identical reasoning `decideCampaignWave` gives for checking
 *    its own opt-in unconditionally.
 * 5. `excluded_recently_texted_back`: idempotency against redial bursts,
 *    checked last, the same position `decideCampaignWave` gives its own
 *    already-sent check.
 */
export function decideMissedCallTextBack(
  candidate: MissedCallCandidateInput,
): MissedCallTextBackDecision {
  if (!candidate.hasContactMatch) {
    return plan(
      "excluded_no_contact_match",
      "the calling number matched no known contact; no captured, consented number to text back",
    );
  }

  if (candidate.isSuppressed) {
    return plan(
      "excluded_suppressed",
      "the contact is on the global suppression list (section 7)",
    );
  }

  if (isActiveConversation(candidate)) {
    return plan(
      "excluded_active_conversation",
      "the contact's sms thread is an active conversation; a generic missed-call text-back never interrupts one",
    );
  }

  if (!candidate.hasSmsConsent) {
    return plan(
      "excluded_no_sms_consent",
      "no express sms consent is on file for this contact (CLAUDE.md section 8: captured AND consented)",
    );
  }

  if (candidate.lastTextBackSentAt !== null) {
    const sinceLastTextBack = candidate.now.getTime() - candidate.lastTextBackSentAt.getTime();
    if (sinceLastTextBack < MISSED_CALL_TEXT_BACK_COOLDOWN_MS) {
      return plan(
        "excluded_recently_texted_back",
        "a missed-call text-back already went out to this contact within the cooldown window",
      );
    }
  }

  return plan(
    "eligible",
    "known and consented contact, no active conversation, not suppressed, not recently texted back",
  );
}
